import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MessageCircle, Send, Phone, Mail, Clock, ChevronDown, Filter } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import communicationService from '../services/communicationService';
import api from '../services/api';
import CommunicationThread from '../components/communications/CommunicationThread';
import NewMessageModal from '../components/communications/NewMessageModal';
import CommunicationStats from '../components/communications/CommunicationStats';

const Communications = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlClientId = searchParams.get('client');
  const urlCaseId = searchParams.get('case');
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedThread, setSelectedThread] = useState(null);
  const autoSelectedRef = useRef(false);
  const [showNewMessageModal, setShowNewMessageModal] = useState(false);
  const [filter, setFilter] = useState(() => localStorage.getItem('comms_filter') || 'all'); // all, unread, sms, email
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [stats, setStats] = useState(null);
  const [sort, setSort] = useState(() => localStorage.getItem('comms_sort') || 'recent'); // recent, oldest, unread, a-z
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [markingAllRead, setMarkingAllRead] = useState(false);

  // Filter preferences state
  const [filterPreferences, setFilterPreferences] = useState({
    hide_unless_tagged: false,
    hide_handled: false,
    only_my_conversations: false,
    only_unresponded: false,
    only_my_assigned_cases: false,
    hide_group_conversations: false,
    exclude_muted: true
  });
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);

  // Count active filters for badge
  const activeFilterCount = Object.entries(filterPreferences).filter(
    ([key, value]) => key !== 'exclude_muted' && value === true
  ).length;

  // Load filter preferences from server
  const loadFilterPreferences = useCallback(async () => {
    try {
      const response = await api.get('/users/preferences/communications');
      setFilterPreferences(response.data);
      setPreferencesLoaded(true);
    } catch (err) {
      // If no preferences exist, use defaults
      if (err.response?.status !== 404) {
        console.error('Error loading filter preferences:', err);
      }
      setPreferencesLoaded(true);
    }
  }, []);

  const loadThreads = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: 1,
        size: 50,
      };

      if (filter === 'unread') {
        params.unread_only = true;
      }

      // Server-side search
      if (debouncedSearch.trim()) {
        params.search = debouncedSearch.trim();
      }

      // Apply filter preferences to API call
      if (filterPreferences.hide_unless_tagged) params.hide_unless_tagged = true;
      if (filterPreferences.hide_handled) params.hide_handled = true;
      if (filterPreferences.only_my_conversations) params.only_my_conversations = true;
      if (filterPreferences.only_unresponded) params.only_unresponded = true;
      if (filterPreferences.only_my_assigned_cases) params.only_my_assigned_cases = true;
      if (filterPreferences.hide_group_conversations) params.hide_group = true;
      if (filterPreferences.exclude_muted !== undefined) params.exclude_muted = filterPreferences.exclude_muted;

      if (urlClientId) params.client_id = urlClientId;
      if (urlCaseId) params.case_id = urlCaseId;

      const response = await communicationService.getThreads(params);
      setThreads(response.threads);
      // Auto-select first thread when deep-linking via URL params
      if ((urlClientId || urlCaseId) && response.threads.length > 0 && !autoSelectedRef.current) {
        autoSelectedRef.current = true;
        setSelectedThread(response.threads[0]);
      }
    } catch (err) {
      console.error('Error loading communication threads:', err);
    } finally {
      setLoading(false);
    }
  }, [filter, filterPreferences, debouncedSearch, urlClientId, urlCaseId]);

  const loadStats = useCallback(async () => {
    try {
      const statsData = await communicationService.getStats(30);
      setStats(statsData);
    } catch (err) {
      console.error('Error loading communication stats:', err);
    }
  }, []);

  // Load filter preferences on mount
  useEffect(() => {
    loadFilterPreferences();
  }, [loadFilterPreferences]);

  // Load threads after preferences are loaded
  useEffect(() => {
    if (preferencesLoaded) {
      loadThreads();
    }
  }, [loadThreads, preferencesLoaded]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  // Persist filter to localStorage
  useEffect(() => {
    localStorage.setItem('comms_filter', filter);
  }, [filter]);

  // Persist sort to localStorage
  useEffect(() => {
    localStorage.setItem('comms_sort', sort);
  }, [sort]);

  // Debounce search term (300ms)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleThreadClick = async (thread) => {
    setSelectedThread(thread);

    // Mark messages as read when thread is opened
    if (thread.unread_count > 0) {
      try {
        await communicationService.markClientMessagesAsRead(thread.client_id);
        // Reload threads to update unread counts
        loadThreads();
        loadStats();
      } catch (err) {
        console.error('Error marking messages as read:', err);
      }
    }
  };

  const handleMarkAllAsRead = async () => {
    setMarkingAllRead(true);
    try {
      await communicationService.markAllAsRead();
      loadThreads();
      loadStats();
    } catch (err) {
      console.error('Error marking all as read:', err);
    } finally {
      setMarkingAllRead(false);
    }
  };

  const handleNewMessage = () => {
    setShowNewMessageModal(true);
  };

  const handleMessageSent = () => {
    setShowNewMessageModal(false);
    loadThreads();
    loadStats();
  };

  const filteredThreads = threads.filter(item => {
    // Search is now server-side, only apply local type filters
    if (filter === 'unread') {
      return item.unread_count > 0;
    } else if (filter === 'sms') {
      return (item.messages || []).some(msg => msg.type === 'sms');
    } else if (filter === 'email') {
      return (item.messages || []).some(msg => msg.type === 'email');
    }

    return true;
  }).sort((a, b) => {
    switch (sort) {
      case 'recent':
        return new Date(b.last_message_at) - new Date(a.last_message_at);
      case 'oldest':
        return new Date(a.last_message_at) - new Date(b.last_message_at);
      case 'unread':
        return b.unread_count - a.unread_count;
      case 'a-z':
        return a.client_name.localeCompare(b.client_name);
      default:
        return 0;
    }
  });

  const formatLastMessageTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const getMessageTypeIcon = (type) => {
    switch (type) {
      case 'sms':
        return <MessageCircle className="w-4 h-4" />;
      case 'email':
        return <Mail className="w-4 h-4" />;
      case 'phone_call':
        return <Phone className="w-4 h-4" />;
      default:
        return <MessageCircle className="w-4 h-4" />;
    }
  };

  if (loading && threads.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="animate-pulse space-y-4">
              <div className="h-8 bg-gray-300 rounded w-1/4"></div>
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map(i => (
                  <div key={i} className="h-20 bg-gray-300 rounded"></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Communications</h1>
              <p className="text-gray-600">Manage client communications and messaging</p>
            </div>
            <div className="flex items-center gap-3">
              {/* Filter Preferences Button */}
              <button
                onClick={() => navigate('/settings', { state: { activeTab: 'communication-preferences' } })}
                className={`relative px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                  activeFilterCount > 0
                    ? 'bg-gray-100 text-yellow-600 hover:bg-blue-200'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                title="Configure communication filters"
              >
                <Filter className="w-4 h-4" />
                Filters
                {activeFilterCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-yellow-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                    {activeFilterCount}
                  </span>
                )}
              </button>
              <button
                onClick={handleMarkAllAsRead}
                disabled={markingAllRead}
                className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 flex items-center gap-2 transition-colors disabled:opacity-50"
                title="Mark all messages as read"
              >
                {markingAllRead ? 'Marking...' : 'Mark all read'}
              </button>
              <button
                onClick={handleNewMessage}
                className="bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600 flex items-center gap-2"
              >
                <Send className="w-4 h-4" />
                New Message
              </button>
            </div>
          </div>
        </div>

        {/* Active Filters Banner */}
        {activeFilterCount > 0 && (
          <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-yellow-600">
              <Filter className="w-4 h-4" />
              <span>
                {activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''} active:
                {filterPreferences.hide_unless_tagged && ' Hide unless tagged,'}
                {filterPreferences.hide_handled && ' Hide handled,'}
                {filterPreferences.only_my_conversations && ' My conversations only,'}
                {filterPreferences.only_unresponded && ' Unresponded only,'}
                {filterPreferences.only_my_assigned_cases && ' My assigned cases,'}
                {filterPreferences.hide_group_conversations && ' Hide groups'}
              </span>
            </div>
            <button
              onClick={() => navigate('/settings', { state: { activeTab: 'communication-preferences' } })}
              className="text-sm text-yellow-600 hover:text-gray-800 font-medium"
            >
              Edit Filters
            </button>
          </div>
        )}

        {/* Stats Dashboard */}
        {stats && <CommunicationStats stats={stats} />}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Threads List */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm">
              <div className="p-4 border-b">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold">By Client</h2>
                  <div className="relative">
                    <button
                      onClick={() => setShowSortMenu(!showSortMenu)}
                      className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1 px-2 py-1 hover:bg-gray-100 rounded"
                      title={`Sorted by: ${sort}`}
                    >
                      Sort: {sort === 'recent' ? 'Recent' : sort === 'oldest' ? 'Oldest' : sort === 'unread' ? 'Unread' : 'A-Z'}
                      <ChevronDown className="w-4 h-4" />
                    </button>
                    {showSortMenu && (
                      <div className="absolute right-0 mt-1 bg-white border rounded-lg shadow-lg z-10 min-w-[150px]">
                        {[
                          { value: 'recent', label: 'Recent' },
                          { value: 'oldest', label: 'Oldest' },
                          { value: 'unread', label: 'Unread' },
                          { value: 'a-z', label: 'A-Z' }
                        ].map(option => (
                          <button
                            key={option.value}
                            onClick={() => {
                              setSort(option.value);
                              setShowSortMenu(false);
                            }}
                            className={`w-full text-left px-4 py-2 text-sm ${
                              sort === option.value
                                ? 'bg-gray-50 text-yellow-600 font-medium'
                                : 'text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Search */}
                <input
                  type="text"
                  placeholder="Search conversations..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full p-2 border rounded-lg text-sm"
                />
                
                {/* Filters */}
                <div className="flex gap-2 mt-3">
                  {[
                    { key: 'all', label: 'All' },
                    { key: 'unread', label: 'Unread' },
                    { key: 'sms', label: 'SMS' },
                    { key: 'email', label: 'Email' }
                  ].map(filterOption => (
                    <button
                      key={filterOption.key}
                      onClick={() => setFilter(filterOption.key)}
                      className={`px-3 py-1 text-xs rounded-full border ${
                        filter === filterOption.key
                          ? 'bg-gray-100 text-yellow-600 border-gray-200'
                          : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {filterOption.label}
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="max-h-96 overflow-y-auto">
                {filteredThreads.length === 0 ? (
                  <div className="p-6 text-center text-gray-500">
                    <MessageCircle className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                    <p>No conversations found</p>
                  </div>
                ) : (
                  filteredThreads.map(thread => (
                    <div
                      key={thread.client_id}
                      onClick={() => handleThreadClick(thread)}
                      className={`p-4 border-b cursor-pointer hover:bg-gray-50 ${
                        selectedThread?.client_id === thread.client_id ? 'bg-gray-50' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium text-gray-900 truncate">
                              {thread.client_name}
                            </h3>
                            {thread.unread_count > 0 && (
                              <span className="bg-red-500 text-white text-xs rounded-full px-2 py-1 min-w-[20px] text-center">
                                {thread.unread_count}
                              </span>
                            )}
                          </div>

                          {thread.case_title && (
                            <p className="text-sm text-gray-500 truncate">
                              Case: {thread.case_title}
                            </p>
                          )}

                          {thread.messages.length > 0 && (
                            <div className="flex items-center gap-2 mt-1">
                              {getMessageTypeIcon(thread.messages[0].type)}
                              <p className="text-sm text-gray-600 truncate flex-1">
                                {thread.messages[0].direction === 'outbound' ? 'You: ' : ''}
                                {thread.messages[0].content}
                              </p>
                            </div>
                          )}
                        </div>

                        <div className="text-xs text-gray-400 ml-2">
                          <Clock className="w-3 h-3 inline mr-1" />
                          {formatLastMessageTime(thread.last_message_at)}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Thread Detail */}
          <div className="lg:col-span-2">
            {selectedThread ? (
              <CommunicationThread
                thread={selectedThread}
                onMessageSent={handleMessageSent}
                onRefresh={loadThreads}
              />
            ) : (
              <div className="bg-white rounded-lg shadow-sm p-8">
                <div className="text-center text-gray-500">
                  <MessageCircle className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <h3 className="text-lg font-medium mb-2">Select a conversation</h3>
                  <p>Choose a conversation from the left to view messages</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* New Message Modal */}
      {showNewMessageModal && (
        <NewMessageModal
          onClose={() => setShowNewMessageModal(false)}
          onMessageSent={handleMessageSent}
        />
      )}
    </div>
  );
};

export default Communications;